import { makeStyles, Tooltip } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EventPreviewPopper, useDialogs, useClientOptions, useUsers } from '@paintscout/ui-business'
import moment from 'moment-timezone'
import React, { useState } from 'react'
import type { EventProps } from 'react-big-calendar'
import type { CalendarEvent } from 'paintscout'
import classnames from 'classnames'
import { USER_COLOR_MAP } from '@paintscout/util'
import { translateEventType } from '@paintscout/util/builder'
import { useCancelEventFlow } from 'src/hooks'
import history from 'shared/util/routing/history'
import { EventDialog, ViewEventsDialog } from '../../../dialogs'
import { useCalendar } from '../CalendarContext'

const useStyles = makeStyles<Theme, { color: string }>((theme) => ({
  root: {
    display: 'flex',
    backgroundColor: ({ color }) => USER_COLOR_MAP[color]?.chipBg || USER_COLOR_MAP[color]?.light || `${color}50`,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(11),
    height: '24px',
    position: 'relative',
    zIndex: 1000,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ color }) => USER_COLOR_MAP[color]?.light || `${color}80`
      }
    }
  },
  timeWrapper: {
    backgroundColor: ({ color }) => color,
    borderRadius: theme.borderRadius.md,
    paddingRight: '5px',
    minWidth: '65px',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    height: '100%',
    zIndex: 2
  },
  eventType: {
    color: ({ color }) =>
      USER_COLOR_MAP[color]?.chipText || USER_COLOR_MAP[color]?.dark || theme.palette.text.secondary,
    paddingLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  grouped: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    padding: theme.spacing(0.25, 0.5),
    boxSizing: 'border-box',
    gap: theme.spacing(0.75),
    zIndex: 1000
  },
  leftAccent: {
    width: '4px',
    height: '70%',
    flexShrink: 0,
    borderRadius: '999px',
    backgroundColor: ({ color }) => USER_COLOR_MAP[color]?.chipPill || color
  },
  estimatorName: {
    color: ({ color }) =>
      USER_COLOR_MAP[color]?.chipText || USER_COLOR_MAP[color]?.dark || theme.palette.text.secondary,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center'
  },
  eventCount: {
    width: 'fit-content',
    minWidth: '18px',
    flexShrink: 0,
    padding: theme.spacing(0, 0.75),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    backgroundColor: ({ color }) => USER_COLOR_MAP[color]?.chipPill || color,
    borderRadius: theme.borderRadius.md,
    fontWeight: 500,
    height: '18px',
    boxSizing: 'border-box',
    lineHeight: 1
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 3px,
          transparent 3px,
          transparent 12px
        )
      `
    }
  }
}))

export default function MonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props

  // below: AKA if only one estimator is selected
  if (event.subsequentEvents === undefined) {
    return <SingleMonthEvent {...props} />
  } else {
    return <GroupedMonthEvent {...props} />
  }
}

// Shared popper wiring for single-event month-view blocks. Mirrors WeekEvent.tsx
// so a click on a 1-event block opens the same EventPreviewPopper used by the
// week/day views. event.start/end were shifted in Calendar.tsx for grid
// rendering, so we resolve back to the original UTC event before passing it to
// the popper or EventDialog (their `.tz(timezone)` formatting depends on it).
function useMonthEventPopper(event: CalendarEvent) {
  const { events: contextEvents, updateEvent, deleteEvent } = useCalendar()
  const { users } = useUsers()
  const { openDialog, dismissDialog } = useDialogs()
  const cancelEventFlow = useCancelEventFlow()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const originalEvent = contextEvents.find((evt) => evt.id === event.id) ?? event

  const handleEditEvent = () => {
    openDialog(EventDialog, {
      event: originalEvent,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
        setAnchorEl(null)
      }
    })
  }

  const handleCancelEvent = (evt: CalendarEvent) =>
    cancelEventFlow(evt, (cancelled, template) => {
      deleteEvent(cancelled, template)
      setAnchorEl(null)
    })

  const openPopper = (target: HTMLElement) => setAnchorEl(anchorEl ? null : target)

  const popperElement = (
    <EventPreviewPopper
      event={originalEvent}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      users={users}
      onClose={() => setAnchorEl(null)}
      onEditEvent={handleEditEvent}
      onCancelEvent={handleCancelEvent}
      onNavigate={(path) => history.push(path)}
      minWidth={330}
    />
  )

  return { openPopper, popperElement }
}

function SingleMonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props
  const { options } = useClientOptions()
  const color = event.estimator.color
  const classes = useStyles({ color })
  const { openPopper, popperElement } = useMonthEventPopper(event)

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    openPopper(e.currentTarget)
  }

  if (event.eventType === '_google-calendar_') {
    const transparency = event.transparency === 'transparent' ? 'Free' : 'Busy'
    return (
      <Tooltip content={`Imported from Google (${transparency})`} hideIcon disableWrapper>
        <div className={classnames(classes.root, classes.integrationOverlay, 'rbc-event-imported')}>
          <div className={classes.timeWrapper}>{moment(event.start).format('h:mma')}</div>
          <div className={classes.eventType}>
            <p style={{ position: 'relative', zIndex: 2 }}>Event</p>
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <>
      <div className={classes.root} onClick={handleClick}>
        <div className={classes.timeWrapper}>{moment(event.start).format('h:mma')}</div>
        <div className={classes.eventType}>{translateEventType({ eventType: event.eventType, options })}</div>
      </div>
      {popperElement}
    </>
  )
}

function GroupedMonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props
  const color = event.estimator.color
  const classes = useStyles({ color })
  const { events: contextEvents, addEvent, updateEvent, deleteEvent, selectedUsers } = useCalendar()
  const { openDialog } = useDialogs()
  const { openPopper, popperElement } = useMonthEventPopper(event)

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!event.subsequentEvents || event.subsequentEvents.length === 0) {
      openPopper(e.currentTarget)
      return
    }

    // Resolve grid-shifted events back to their original UTC versions so
    // ViewEventsDialog's `.tz(timezone)` formatting renders correctly.
    const lookup = (evt: CalendarEvent) => contextEvents.find((c) => c.id === evt.id) ?? evt
    openDialog(ViewEventsDialog, {
      events: [lookup(event), ...event.subsequentEvents.map(lookup)],
      addEvent,
      updateEvent,
      deleteEvent,
      selectedUsers
    })
  }

  return (
    <>
      <div className={classnames(classes.grouped, classes.root)} onClick={handleClick}>
        <div className={classes.leftAccent} />
        <div className={classes.estimatorName}>{event.estimator.name}</div>
        <div className={classes.eventCount}>{event.subsequentEvents?.length + 1}</div>
      </div>
      {popperElement}
    </>
  )
}
